/*
 * ET - Collection Slider (sections/et-collection-slider.liquid)
 * <et-collection-slider> wraps heading · .swiper · progress pill. Swiper comes from window.etLoadSwiper() (et-base-head).
 * Card widths come from the CSS (slidesPerView: 'auto'), driven by the "Cards per view" settings: mobile below 750px,
 * tablet from 750px, desktop from 990px. Gaps and offsets are fixed to the Figma and mirrored in the CSS for the pre-init layout.
 * The element creates one Swiper when connected and destroys it when disconnected, so a theme editor re-render
 * (shopify:section:load / unload) never leaves a duplicate instance. Blocks selected in the editor slide into view.
 * Desktop layout "Grid" (data-desktop-layout="grid"): from 990px the cards are a CSS grid, so Swiper exists only below
 * 990px; crossing the breakpoint builds or destroys it (destroy cleans its inline styles off the track and slides).
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-collection-slider')) return;

  const SLIDE_SELECTOR = '[data-et-cs-slide]';
  const PROGRESS_THUMB_SIZE = 39;
  const GRID_QUERY = '(min-width: 990px)';

  const toPerView = (value, fallback) => {
    const number = parseFloat(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  };

  class EtCollectionSlider extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.destroy();
      if (this.gridQuery) {
        this.gridQuery.removeEventListener('change', this.onGridChange);
        this.gridQuery = null;
      }
    }

    get showsGrid() {
      return Boolean(this.gridQuery && this.gridQuery.matches);
    }

    watchGrid() {
      if (this.gridQuery || this.dataset.desktopLayout !== 'grid' || typeof window.matchMedia !== 'function') return;
      this.gridQuery = window.matchMedia(GRID_QUERY);
      this.onGridChange = () => (this.showsGrid ? this.destroy() : this.init());
      this.gridQuery.addEventListener('change', this.onGridChange);
    }

    get options() {
      const perViewMobile = toPerView(this.dataset.slidesMobile, 2.7);
      const perViewTablet = toPerView(this.dataset.slidesTablet, 3.5);
      const perViewDesktop = toPerView(this.dataset.slidesDesktop, 6);
      const slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      const loop = this.dataset.loop === 'true';
      const canLoop = (perView) => loop && slideCount > Math.ceil(perView);
      const delay = parseInt(this.dataset.autoplaySpeed, 10) || 3500;
      const reducedMotion =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const progressTrack = this.querySelector('[data-et-cs-progress]');
      const progressWrap = this.querySelector('[data-et-cs-progress-wrap]');
      const syncProgress = (swiper) => {
        if (progressWrap && swiper) progressWrap.hidden = Boolean(swiper.isLocked);
      };

      const options = {
        slidesPerView: 'auto',
        spaceBetween: 12,
        slidesOffsetBefore: 24,
        slidesOffsetAfter: 24,
        speed: 400,
        loop: canLoop(perViewMobile),
        grabCursor: true,
        watchOverflow: true,
        autoplay:
          this.dataset.autoplay === 'true' && !reducedMotion
            ? { delay, disableOnInteraction: false, pauseOnMouseEnter: true }
            : false,
        breakpoints: {
          750: {
            spaceBetween: 16,
            slidesOffsetBefore: 48,
            slidesOffsetAfter: 48,
            loop: canLoop(perViewTablet)
          },
          990: {
            spaceBetween: 16,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            loop: canLoop(perViewDesktop)
          }
        },
        on: {
          afterInit: syncProgress,
          lock: syncProgress,
          unlock: syncProgress
        }
      };

      if (progressTrack) {
        options.scrollbar = { el: progressTrack, draggable: true, dragSize: PROGRESS_THUMB_SIZE };
      }

      return options;
    }

    init() {
      this.watchGrid();
      const swiperEl = this.querySelector('[data-et-cs-swiper]');
      if (!swiperEl || this.swiper || this.loading || this.showsGrid || typeof window.etLoadSwiper !== 'function') return;

      this.loading = true;
      window.etLoadSwiper().then((Swiper) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper || this.showsGrid) return;
        this.swiper = new Swiper(swiperEl, this.options);
      });
    }

    destroy() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    selectSlide(slide) {
      if (!this.swiper) return;
      const index = parseInt(slide.dataset.etCsIndex, 10);
      if (Number.isNaN(index)) return;

      if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
      if (this.swiper.params.loop) this.swiper.slideToLoop(index, 0);
      else this.swiper.slideTo(index, 0);
    }

    resumeAutoplay() {
      if (this.swiper && this.swiper.params.autoplay && this.swiper.params.autoplay.enabled && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    }
  }

  window.customElements.define('et-collection-slider', EtCollectionSlider);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-collection-slider')) : [];

  const slideFrom = (event) => (event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null);

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.init()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = slideFrom(event);
    const slider = slide && slide.closest('et-collection-slider');
    if (slider) slider.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slide = slideFrom(event);
    const slider = slide && slide.closest('et-collection-slider');
    if (slider) slider.resumeAutoplay();
  });
})();
